import {
	CommonModule,
	CurrencyPipe,
	registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import {
	ChangeDetectionStrategy,
	Component,
	effect,
	EventEmitter,
	inject,
	Input,
	input,
	OnChanges,
	Output,
	Signal,
	SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import dayjs from 'dayjs';
import { Instance as FlatPickrInstance } from 'flatpickr/dist/types/instance';
import { BaseOptions as FlatPickrBaseOptions } from 'flatpickr/dist/types/options';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	map,
	Observable,
	tap,
} from 'rxjs';

import { AvailabilityDatesInterface } from '@valk-nx/availability-dates-store/availability-dates.interface';
import {
	CalendarComponent,
	FlatPickrEvent,
	FlatPickrEventType,
} from '@valk-nx/components/ui-calendar/src/lib/calendar';
import { addOptionalPricing } from '@valk-nx/components/ui-date-picker/src/lib/date-picker.helper';
import { DatePickerInterface } from '@valk-nx/components/ui-date-picker/src/lib/date-picker.interface';
import { StepperComponent } from '@valk-nx/components/ui-stepper/src/lib/stepper';
import { FlatpickrFacade } from '@valk-nx/flatpickr-store/flatpickr.facade';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

import { AvailabilityWidgetInputComponent } from '../availability-input/availability-input';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget-input-date-range`,
	templateUrl: './availability-input-date-range.html',
	imports: [
		AngularSvgIconModule,
		AvailabilityWidgetInputComponent,
		CalendarComponent,
		CommonModule,
		LetDirective,
		StepperComponent,
		TranslatePipe,
	],
	providers: [CurrencyPipe, FlatpickrFacade],
})
export class AvailabilityWidgetInputDateRangeComponent implements OnChanges {
	private readonly currencyPipe = inject(CurrencyPipe);
	private readonly flatpickrFacade = inject(FlatpickrFacade);
	private readonly translate = inject(TranslateService);
	private readonly viewport = inject(ViewPortService);

	@Input() className = '';
	@Input({ required: true }) availability: AvailabilityDatesInterface;
	@Input({ required: true }) config: Partial<FlatPickrBaseOptions>;
	@Input() initialArrivalDate: Date;
	@Input() initialDepartureDate: Date;
	@Input() prettyDateFormat = 'dd D MMM';
	@Input() isLoading = false;
	@Input() jumpToFirstAvailableDate = false;
	@Input() stepperConfig = { useStepper: false, min: 1, max: undefined };
	@Input() numberOfNights: number | null = null;

	hideRangePrices = input<boolean>(false);
	resetDateRange = input<number>();

	@Input()
	set isOpen(value: boolean) {
		this._isOpen = value;
	}

	@Output() datePickerModelChanged = new EventEmitter<DatePickerInterface>();
	@Output() emitHasError = new EventEmitter<boolean>();
	@Output() emitMonthChange = new EventEmitter<string>();
	@Output() emitAvailabilityButton = new EventEmitter<Event>();
	@Output() emitBackButton = new EventEmitter<Event>();
	@Output() initialDatesNotAvailable = new EventEmitter<void>();
	@Output() emitIsPopoverOpen = new EventEmitter<boolean>();
	@Output() stepperUpdated = new EventEmitter<number>();

	currency = input('EUR');
	showPrices = input(false);

	config$: Observable<Partial<FlatPickrBaseOptions>>;
	filteredCalendarData$: Observable<string[]>;
	selectedDates$: Observable<Date[]>;
	selectedDates: Signal<Date[]>;
	locale: string;
	prettyStartDate$: Observable<string>;
	prettyEndDate$: Observable<string>;
	prettyCombinedDates$: Observable<string>;

	recalculateCalendarData$ = new BehaviorSubject<void>(undefined);
	isSmallTablet$: Observable<boolean>;
	isSmallTablet: Signal<boolean>;
	_isOpen = false;

	constructor() {
		// Clear date range when reset trigger changes
		effect(() => {
			const trigger = this.resetDateRange();
			if (trigger !== undefined) {
				this.flatpickrFacade.clearFlatpickrInstance();
			}
		});

		this.locale =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();
		this.isSmallTablet$ = this.viewport.isSmallTablet$;
		this.isSmallTablet = toSignal(this.viewport.isSmallTablet$);
		this.config$ = this.adjustConfigToScreenSize();

		this.selectedDates$ = this.flatpickrFacade.selectSelectedDates$;
		this.selectedDates = toSignal(this.selectedDates$, {
			initialValue: [],
		});

		this.prettyStartDate$ = this.flatpickrFacade.selectStartDate$.pipe(
			map((startDate) => this.formatDate(startDate)),
		);

		this.prettyEndDate$ = this.flatpickrFacade.selectEndDate$.pipe(
			map((endDate) => this.formatDate(endDate)),
		);

		this.prettyCombinedDates$ = combineLatest([
			this.prettyStartDate$,
			this.prettyEndDate$,
		]).pipe(
			map(([prettyStartDate, prettyEndDate]) => {
				if (!prettyStartDate) {
					return '';
				}
				if (!prettyEndDate) {
					return prettyStartDate;
				}
				return `${prettyStartDate} - ${prettyEndDate}`;
			}),
		);

		this.filteredCalendarData$ = this.filterCalendarData();

		combineLatest([
			this.flatpickrFacade.selectStartDate$,
			this.flatpickrFacade.selectEndDate$,
		])
			.pipe(
				takeUntilDestroyed(),
				tap(([arrivalDate, departureDate]) => {
					this.datePickerModelChanged.emit({
						arrivalDate,
						departureDate,
					});
				}),
			)
			.subscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['availability']) {
			this.recalculateCalendarData$.next();
		}
		if (changes['numberOfNights']) {
			this.flatpickrFacade.setNights(this.numberOfNights);
			this.flatpickrFacade.setEndDateAccordingToNights();
		}

		const initialDatesChanged =
			changes['initialArrivalDate'] || changes['initialDepartureDate'];
		if (
			(initialDatesChanged || this.selectedDates().length === 0) &&
			this.areDatesValid(
				this.initialArrivalDate,
				this.initialDepartureDate,
			)
		) {
			const dates = [this.initialArrivalDate, this.initialDepartureDate];

			this.flatpickrFacade.setDatesAndUpdateInCalendar(dates);
		}
	}

	popoverStateChanged(isOpen: boolean) {
		this._isOpen = isOpen;

		this.emitIsPopoverOpen.emit(this._isOpen);
	}

	private areDatesValid(arrivalDate: Date, departureDate: Date): boolean {
		const isArrivalValid =
			arrivalDate instanceof Date && !Number.isNaN(arrivalDate.getTime());
		const isDepartureValid =
			departureDate instanceof Date &&
			!Number.isNaN(departureDate.getTime());

		return isArrivalValid && isDepartureValid;
	}

	private setInitialDates(instance: FlatPickrInstance): void {
		if (
			!this.areDatesValid(
				this.initialArrivalDate,
				this.initialDepartureDate,
			)
		) {
			return;
		}

		const dates = [this.initialArrivalDate, this.initialDepartureDate];

		// Set dates in flatpickr instance (visual calendar)
		instance.setDate(dates, false);

		// Update facade state (for the input field display)
		this.flatpickrFacade.setDates(dates);
		this.flatpickrFacade.setStartDate(dates[0]);
		this.flatpickrFacade.setEndDate(dates[1]);

		// Calculate and set nights
		const nights = dayjs(dates[1]).diff(dates[0], 'day');
		this.flatpickrFacade.setNights(nights);
	}

	isArrivalDateAvailable(arrivalDate: Date): boolean {
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');

		return this.availability && !!this.availability[arrivalDateKey];
	}

	isDepartureDateAvailable(arrivalDate: Date, departureDate: Date): boolean {
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const departureDateKey = this.formatDate(departureDate, 'YYYY-MM-DD');
		const availableDepartures = this.availability?.[arrivalDateKey];

		if (!availableDepartures) {
			return false;
		}

		return !!availableDepartures[departureDateKey];
	}

	formatDate(
		date: string | Date | dayjs.Dayjs | undefined,
		format = this.prettyDateFormat,
	): string {
		return date ? `${dayjs(date).format(format)}` : '';
	}

	adjustConfigToScreenSize(): Observable<Partial<FlatPickrBaseOptions>> {
		return this.isSmallTablet$.pipe(
			distinctUntilChanged(),
			map((isSmallTablet) => {
				return {
					...this.config,
					showMonths: isSmallTablet ? 1 : this.config?.showMonths,
				};
			}),
			tap((config) => {
				this.flatpickrFacade.updateShowMonths(config.showMonths);
			}),
		);
	}

	filterCalendarData(): Observable<string[]> {
		return combineLatest([
			this.recalculateCalendarData$,
			this.selectedDates$,
		]).pipe(
			map(([_, selectedDates]) => {
				this.checkForInvalidDates(selectedDates);

				if (selectedDates.length === 1) {
					const arrivalDateKey = this.formatDate(
						selectedDates[0],
						'YYYY-MM-DD',
					);
					const availableDepartures =
						this.availability?.[arrivalDateKey];
					return availableDepartures
						? Object.keys(availableDepartures)
						: [];
				} else if (this.availability) {
					return Object.keys(this.availability);
				} else {
					return [];
				}
			}),
		);
	}

	checkForInvalidDates(selectedDates: Date[]): void {
		if (selectedDates.length === 0) {
			return;
		}

		const [arrivalDate, departureDate] = selectedDates;

		const isArrivalInvalid = !this.isArrivalDateAvailable(arrivalDate);
		const isDepartureInvalid =
			selectedDates.length > 1 &&
			!this.isDepartureDateAvailable(arrivalDate, departureDate);

		if (
			(!this.stepperConfig.useStepper &&
				(isArrivalInvalid || isDepartureInvalid)) ||
			(this.stepperConfig.useStepper && isArrivalInvalid)
		) {
			this.flatpickrFacade.clearFlatpickrInstance();
		}
	}

	onDatePickerEmitter(event: FlatPickrEvent): void {
		if (event.type === FlatPickrEventType.OnReady) {
			this.setInitialDates(event.instance);
		}

		if (event.type === FlatPickrEventType.OnValueUpdate) {
			if (this.numberOfNights && this.numberOfNights > 0) {
				this.flatpickrFacade.setEndDate(null);
				this.flatpickrFacade.setStartDate(event.selectedDates[0]);
				this.flatpickrFacade.setNights(this.numberOfNights);
				this.flatpickrFacade.setEndDateAccordingToNights();

				if (!this.isSmallTablet()) {
					this.popoverStateChanged(false);
				}
			} else {
				this.flatpickrFacade.setDatesAndUpdateNights(
					event.selectedDates,
				);
				this.recalculateCalendarData$.next();
			}

			// BUG: somehow the spy doesnt get called, yet it hits it when debugging
			// istanbul ignore next
			if (event.selectedDates.length > 1) {
				if (!this.isSmallTablet()) {
					this.popoverStateChanged(false);
				}
			}
		}
	}

	onNumberOfNightsChange(numberOfNights: number) {
		this.numberOfNights = numberOfNights;
		this.stepperUpdated.emit(numberOfNights);
	}

	onEmitMonthChange(newDate: string) {
		this.emitMonthChange.emit(newDate);
	}

	onEmitHasError(hasError: boolean) {
		this.emitHasError.emit(hasError);
	}

	onEmitBackButton(event: Event) {
		this.popoverStateChanged(false);
		this.emitBackButton.emit(event);
	}

	onSubmitButtonClick() {
		this.popoverStateChanged(false);
		this.emitAvailabilityButton.emit();
	}

	addPricingToDayElement({
		dayElement,
		instance,
	}: {
		dayElement: HTMLElement;
		instance: FlatPickrInstance;
	}) {
		const formattedDate = dayjs(dayElement['dateObj']).format('YYYY-MM-DD');
		const date = this.availability?.[formattedDate];

		if (date && this.showPrices()) {
			const checkoutDate = Object.keys(date)[0];

			if (checkoutDate && date[checkoutDate]?.price > 0) {
				const absolutePrice = date[checkoutDate].price / 100;
				const price = this.currencyPipe.transform(
					absolutePrice,
					this.currency(),
					'symbol-narrow',
					'1.0-0',
					this.locale,
				);

				addOptionalPricing(instance, dayElement, price.toString());
			}
		}
	}
}
